/**
 * Network Status Hook - Sprint 31: Offline & Resilience
 *
 * Detects browser online/offline status and provides state for UI indicators.
 * Used to show degraded mode banners and disable actions when offline.
 */
import { useState, useEffect, useCallback } from 'react';

export interface NetworkStatus {
  /** True if browser reports online status */
  isOnline: boolean;
  /** True if we've confirmed API reachability (not just browser online) */
  isApiReachable: boolean;
  /** Timestamp of last successful API call */
  lastOnlineAt: number | null;
  /** Time since last online in seconds */
  offlineDuration: number;
  /** Manually trigger a connectivity check */
  checkConnectivity: () => Promise<boolean>;
}

/**
 * Hook to monitor network connectivity status
 *
 * @param apiHealthEndpoint - Optional endpoint to ping for API reachability
 * @param checkIntervalMs - Interval to check API health when online (default: 30s)
 */
export function useNetworkStatus(
  apiHealthEndpoint = '/api/v1/health/status',
  checkIntervalMs = 30000
): NetworkStatus {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isApiReachable, setIsApiReachable] = useState(true);
  const [lastOnlineAt, setLastOnlineAt] = useState<number | null>(
    navigator.onLine ? Date.now() : null
  );
  const [offlineDuration, setOfflineDuration] = useState(0);

  // Check API health by pinging endpoint
  const checkConnectivity = useCallback(async (): Promise<boolean> => {
    if (!navigator.onLine) {
      setIsApiReachable(false);
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // Use GET instead of HEAD since FastAPI doesn't support HEAD on the health endpoint
      const response = await fetch(apiHealthEndpoint, {
        method: 'GET',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const reachable = response.ok;
      setIsApiReachable(reachable);

      if (reachable) {
        setLastOnlineAt(Date.now());
        setOfflineDuration(0);
      }

      return reachable;
    } catch {
      setIsApiReachable(false);
      return false;
    }
  }, [apiHealthEndpoint]);

  // Browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setLastOnlineAt(Date.now());
      setOfflineDuration(0);
      // Verify API is actually reachable
      checkConnectivity();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsApiReachable(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [checkConnectivity]);

  // Periodic API health check when online
  useEffect(() => {
    if (!isOnline) return;

    const intervalId = setInterval(() => {
      checkConnectivity();
    }, checkIntervalMs);

    return () => clearInterval(intervalId);
  }, [isOnline, checkIntervalMs, checkConnectivity]);

  // Track offline duration
  useEffect(() => {
    if (isOnline && isApiReachable) return;

    const intervalId = setInterval(() => {
      if (lastOnlineAt) {
        setOfflineDuration(Math.floor((Date.now() - lastOnlineAt) / 1000));
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [isOnline, isApiReachable, lastOnlineAt]);

  return {
    isOnline,
    isApiReachable,
    lastOnlineAt,
    offlineDuration,
    checkConnectivity,
  };
}

/**
 * Format offline duration for display
 */
export function formatOfflineDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  } else if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
}
